// Hash-based router. Default = clickable mockups (P0-10) at #/mocks.
// The P0-02 health check is still available at #/health so the staging
// deploy can be smoke-tested the same way as before.
//
// Hash routing (rather than a real router) keeps the whole app a static
// bundle for Cloudflare Pages, no server rewrites needed. TanStack Router
// comes in P1 when the real register / back office ship.

import { useEffect, useState } from "react";
import { AuditLog } from "./audit/AuditLog.tsx";
import { Catalog } from "./catalog/Catalog.tsx";
import { FirearmSale } from "./mocks/FirearmSale.tsx";
import { Handoff } from "./mocks/Handoff.tsx";
import { MockGallery } from "./mocks/MockGallery.tsx";
import { Register } from "./mocks/Register.tsx";
import { TodayBoard } from "./mocks/TodayBoard.tsx";
import { UsedGunIntake } from "./mocks/UsedGunIntake.tsx";
import { SignIn } from "./signin/SignIn.tsx";

type Health = { status: string; version: string; commit: string };

const VERSION = import.meta.env.VITE_APP_VERSION ?? "0.0.1";
const COMMIT = import.meta.env.VITE_GIT_COMMIT ?? "dev";
const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001";

function useHashRoute() {
  const [hash, setHash] = useState(window.location.hash || "#/mocks");
  useEffect(() => {
    const onChange = () => setHash(window.location.hash || "#/mocks");
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return hash;
}

export function App() {
  const hash = useHashRoute();
  const route = hash.replace(/^#\//, "");

  if (route === "mocks" || route === "" || route === "mocks/") return <MockGallery />;
  if (route === "mocks/register") return <Register />;
  if (route === "mocks/today") return <TodayBoard />;
  if (route === "mocks/firearm-sale") return <FirearmSale />;
  if (route === "mocks/used-gun-intake") return <UsedGunIntake />;
  if (route === "mocks/handoff") return <Handoff />;
  if (route === "sign-in") return <SignIn />;
  if (route === "audit") return <AuditLog />;
  if (route === "catalog") return <Catalog />;
  if (route === "health") return <HealthCheck />;
  return <NotFound hash={hash} />;
}

function HealthCheck() {
  const [health, setHealth] = useState<Health | "loading" | "error">("loading");

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/healthz`)
      .then((r) => (r.ok ? (r.json() as Promise<Health>) : Promise.reject(new Error(r.statusText))))
      .then((h) => {
        if (!cancelled) setHealth(h);
      })
      .catch(() => {
        if (!cancelled) setHealth("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <main className="min-h-screen bg-white flex items-center justify-center p-8 font-sans">
      <div className="max-w-xl w-full space-y-6 text-center">
        <h1 className="text-5xl font-semibold text-[#052460]">BetterPOS</h1>
        <p className="text-slate-600">Down Range Guns & Ammo · Maple Grove, MN</p>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-left bg-slate-50 rounded-lg p-6">
          <dt className="font-medium text-slate-700">Version</dt>
          <dd className="font-mono text-slate-900">v{VERSION}</dd>
          <dt className="font-medium text-slate-700">Commit</dt>
          <dd className="font-mono text-slate-900">{COMMIT}</dd>
          <dt className="font-medium text-slate-700">API</dt>
          <dd className="font-mono">
            {health === "loading" && <span className="text-slate-500">checking…</span>}
            {health === "error" && (
              <span className="text-red-600">not responding at {API_URL}</span>
            )}
            {typeof health === "object" && (
              <span className="text-green-700">
                {health.status} · v{health.version}
              </span>
            )}
          </dd>
        </dl>
        <p>
          <a href="#/mocks" className="text-[#052460] underline">
            ← clickable mockups
          </a>
        </p>
      </div>
    </main>
  );
}

function NotFound({ hash }: { hash: string }) {
  return (
    <main className="min-h-screen bg-white flex items-center justify-center p-8">
      <div className="max-w-md text-center">
        <h1 className="text-3xl font-semibold text-[#052460]">Not here</h1>
        <p className="text-slate-600 mt-2">
          <span className="font-mono">{hash}</span> doesn't match any screen.
        </p>
        <a href="#/mocks" className="inline-block mt-4 text-[#052460] underline">
          → Mockup gallery
        </a>
      </div>
    </main>
  );
}
